import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calculator, Heart, Brain, Droplets, Activity, Stethoscope, AlertTriangle } from 'lucide-react';

interface OrganSystem {
  id: string;
  name: string;
  icon: React.ReactNode;
  options: {
    value: string;
    points: number;
    label: string;
    description: string;
  }[];
}

const Index = () => {
  const [scores, setScores] = useState<Record<string, number>>({});
  const [totalScore, setTotalScore] = useState<number>(0);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const organSystems: OrganSystem[] = [
    {
      id: 'respiratory',
      name: 'Lungenfunktion',
      icon: <Activity className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: '≥ 400 mmHg', description: 'PaO₂/FiO₂ ≥ 400 mmHg' },
        { value: '1', points: 1, label: '< 400 mmHg', description: 'PaO₂/FiO₂ < 400 mmHg' },
        { value: '2', points: 2, label: '< 300 mmHg', description: 'PaO₂/FiO₂ < 300 mmHg' },
        { value: '3', points: 3, label: '< 200 mmHg (Beatmung)', description: 'PaO₂/FiO₂ < 200 mmHg unter Beatmung' },
        { value: '4', points: 4, label: '< 100 mmHg (Beatmung)', description: 'PaO₂/FiO₂ < 100 mmHg unter Beatmung' }
      ]
    },
    {
      id: 'renal',
      name: 'Nierenfunktion',
      icon: <Droplets className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: '< 1,2 mg/dL', description: 'Kreatinin < 1,2 mg/dL' },
        { value: '1', points: 1, label: '1,2–1,9 mg/dL', description: 'Kreatinin 1,2–1,9 mg/dL' },
        { value: '2', points: 2, label: '2,0–3,4 mg/dL', description: 'Kreatinin 2,0–3,4 mg/dL' },
        { value: '3', points: 3, label: '3,5–4,9 mg/dL oder Oligurie', description: 'Kreatinin 3,5–4,9 mg/dL oder Oligurie' },
        { value: '4', points: 4, label: '≥ 5 mg/dL oder Anurie', description: 'Kreatinin ≥ 5 mg/dL oder Anurie' }
      ]
    },
    {
      id: 'hepatic',
      name: 'Leberfunktion',
      icon: <Heart className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: '< 1,2 mg/dL', description: 'Bilirubin < 1,2 mg/dL' },
        { value: '1', points: 1, label: '1,2–1,9 mg/dL', description: 'Bilirubin 1,2–1,9 mg/dL' },
        { value: '2', points: 2, label: '2,0–5,9 mg/dL', description: 'Bilirubin 2,0–5,9 mg/dL' },
        { value: '3', points: 3, label: '6–11,9 mg/dL', description: 'Bilirubin 6–11,9 mg/dL' },
        { value: '4', points: 4, label: '≥ 12 mg/dL', description: 'Bilirubin ≥ 12 mg/dL' }
      ]
    },
    {
      id: 'cardiovascular',
      name: 'Kreislauffunktion',
      icon: <Stethoscope className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: 'MAP ≥ 70 mmHg', description: 'Mittlerer arterieller Druck ≥ 70 mmHg' },
        { value: '1', points: 1, label: 'MAP < 70 mmHg', description: 'Mittlerer arterieller Druck < 70 mmHg' },
        { value: '2', points: 2, label: 'Niedrige Katecholamindosis', description: 'Niedrige Katecholamindosis erforderlich' },
        { value: '3', points: 3, label: 'Mittlere Katecholamindosis', description: 'Mittlere Katecholamindosis erforderlich' },
        { value: '4', points: 4, label: 'Hohe Katecholamindosis', description: 'Hohe Katecholamindosis erforderlich' }
      ]
    },
    {
      id: 'coagulation',
      name: 'Blutbild',
      icon: <Activity className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: '≥ 150/nL', description: 'Thrombozytenzahl ≥ 150/nL' },
        { value: '1', points: 1, label: '< 150/nL', description: 'Thrombozytenzahl < 150/nL' },
        { value: '2', points: 2, label: '< 100/nL', description: 'Thrombozytenzahl < 100/nL' },
        { value: '3', points: 3, label: '< 50/nL', description: 'Thrombozytenzahl < 50/nL' },
        { value: '4', points: 4, label: '< 20/nL', description: 'Thrombozytenzahl < 20/nL' }
      ]
    },
    {
      id: 'neurological',
      name: 'ZNS (Glasgow Coma Scale)',
      icon: <Brain className="h-5 w-5" />,
      options: [
        { value: '0', points: 0, label: 'GCS 15', description: 'Glasgow Coma Scale 15' },
        { value: '1', points: 1, label: 'GCS 13–14', description: 'Glasgow Coma Scale 13–14' },
        { value: '2', points: 2, label: 'GCS 10–12', description: 'Glasgow Coma Scale 10–12' },
        { value: '3', points: 3, label: 'GCS 6–9', description: 'Glasgow Coma Scale 6–9' },
        { value: '4', points: 4, label: 'GCS < 6', description: 'Glasgow Coma Scale < 6' }
      ]
    }
  ];

  const handleScoreChange = (organId: string, value: string) => {
    const points = parseInt(value);
    setScores(prev => ({ ...prev, [organId]: points }));
  };

  useEffect(() => {
    const total = Object.values(scores).reduce((sum, score) => sum + score, 0);
    setTotalScore(total);
    setIsComplete(Object.keys(scores).length === organSystems.length);
  }, [scores, organSystems.length]);

  const getScoreInterpretation = (score: number) => {
    if (score === 0) return { level: 'Normal', color: 'bg-green-100 text-green-800', description: 'Keine Organdysfunktion' };
    if (score <= 6) return { level: 'Mild', color: 'bg-yellow-100 text-yellow-800', description: 'Leichte Organdysfunktion' };
    if (score <= 12) return { level: 'Moderat', color: 'bg-orange-100 text-orange-800', description: 'Moderate Organdysfunktion' };
    if (score <= 18) return { level: 'Schwer', color: 'bg-red-100 text-red-800', description: 'Schwere Organdysfunktion' };
    return { level: 'Kritisch', color: 'bg-red-200 text-red-900', description: 'Kritische Organdysfunktion' };
  };

  const interpretation = getScoreInterpretation(totalScore);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calculator className="h-8 w-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">SOFA-Score Rechner</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Sequential Organ Failure Assessment zur Bewertung der Organdysfunktion
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Organ Systems */}
          <div className="lg:col-span-2 space-y-6">
            {organSystems.map((system) => (
              <Card key={system.id} className="shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-lg">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      {system.icon}
                    </div>
                    {system.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={scores[system.id]?.toString() || ""}
                    onValueChange={(value) => handleScoreChange(system.id, value)}
                    className="space-y-3"
                  >
                    {system.options.map((option) => (
                      <div key={option.value} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <RadioGroupItem value={option.value} id={`${system.id}-${option.value}`} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={`${system.id}-${option.value}`} className="cursor-pointer">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {option.points} {option.points === 1 ? 'Punkt' : 'Punkte'}
                              </Badge>
                              <span className="font-medium">{option.label}</span>
                            </div>
                            <p className="text-sm text-gray-600">{option.description}</p>
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Results Panel */}
          <div className="space-y-6">
            {/* Score Display */}
            <Card className="shadow-lg sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  SOFA-Score Ergebnis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-600 mb-2">
                    {totalScore}
                  </div>
                  <div className="text-lg text-gray-600 mb-4">
                    von 24 Punkten
                  </div>
                  {isComplete && (
                    <Badge className={`${interpretation.color} px-4 py-2 text-sm font-medium`}>
                      {interpretation.level}
                    </Badge>
                  )}
                </div>

                <Separator />

                {/* Individual Scores */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-gray-700 mb-3">Einzelbewertungen:</h4>
                  {organSystems.map((system) => (
                    <div key={system.id} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">{system.name}</span>
                      <Badge variant="secondary">
                        {scores[system.id] ?? '–'} {scores[system.id] !== undefined && (scores[system.id] === 1 ? 'Punkt' : 'Punkte')}
                      </Badge>
                    </div>
                  ))}
                </div>

                {!isComplete && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Bitte bewerten Sie alle Organsysteme für eine vollständige Berechnung.
                    </AlertDescription>
                  </Alert>
                )}

                {isComplete && (
                  <div className="space-y-3">
                    <Separator />
                    <div className="text-sm">
                      <p className="font-medium text-gray-700 mb-2">Interpretation:</p>
                      <p className="text-gray-600 mb-3">{interpretation.description}</p>
                      {totalScore >= 2 && (
                        <Alert>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            <strong>Hinweis:</strong> Ein Anstieg ≥ 2 Punkte kann auf eine relevante Organdysfunktion hinweisen (Sepsis-Kriterium).
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Medical Disclaimer */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">Medizinischer Hinweis</p>
                    <p>
                      Der SOFA-Score dient der Einschätzung der Organdysfunktion und ersetzt keine ärztliche Gesamtbeurteilung. 
                      Die Bewertung sollte immer im klinischen Kontext erfolgen.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
